import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, ExternalLink, Facebook, Instagram, Linkedin, Music2, Youtube } from "lucide-react";
import { api, API } from "../lib/api";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { PlayerRadar, ScoreTable } from "../components/PlayerRadar";
import { PlayerCard, initials, cleanName } from "../components/PlayerCard";
import { ShareCard } from "../components/ShareCard";
import { DidYouKnow } from "../components/DidYouKnow";
import { AfterFootball } from "../components/AfterFootball";

const POS_LABEL = { GK: "Goalkeeper", DEF: "Defender", MID: "Midfielder", FWD: "Forward" };

const XIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M18.24 2.25h3.31l-7.23 8.26 8.5 11.24h-6.65l-5.21-6.82-5.96 6.82H1.69l7.49-8.56L1.03 2.25h6.82l4.86 6.43 5.53-6.43Zm-1.16 17.52h1.83L7.02 4.13H5.06l12.02 15.64Z" />
  </svg>
);

const SOCIALS = [
  ["instagram", Instagram, "Instagram"],
  ["x_url", XIcon, "X"],
  ["facebook", Facebook, "Facebook"],
  ["linkedin", Linkedin, "LinkedIn"],
  ["tiktok", Music2, "TikTok"],
  ["youtube", Youtube, "Highlights"],
];

export default function PlayerDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSeo({
    title: data ? `${cleanName(data.player.name)} — Kenyan football archive` : "Player",
    description: data?.player?.bio,
    image: cardUrl("player", slug),
    type: "profile",
  });

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/players/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="mx-auto max-w-[1500px] px-5 py-40 md:px-10" data-testid="player-not-found">
        <h1 className="display-2">Player not found</h1>
        <Link to="/hall-of-fame" className="btn-ghost mt-8">Back to all players</Link>
      </div>
    );

  if (!data) return <div className="py-40 text-center text-zinc-400" data-testid="player-loading">Loading…</div>;

  const { player } = data;
  const neighbours = data.neighbours || {};
  const fact = data.did_you_know;
  const moreFacts = data.did_you_know_more || [];
  const isHof = player.list_type === "hall_of_fame";
  const socials = SOCIALS.filter(([key]) => player[key]);
  const stats = [
    ["Appearances", player.appearances],
    ["Goals", player.goals],
    ["Assists", player.assists],
    ["Caps", player.caps],
    ["Int'l goals", player.national_goals],
  ];

  return (
    <div data-testid="player-detail-page">
      <section className="relative grid items-end border-b border-white/10 bg-[#0b0b0b] lg:grid-cols-2">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{ backgroundImage: "radial-gradient(circle, #000 1px, transparent 1px)", backgroundSize: "18px 18px" }}
          aria-hidden="true"
        />
        <div className="relative z-10 px-5 pb-12 pt-12 md:px-10 lg:pb-20 lg:pt-20">
          <Link to="/hall-of-fame" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-white/70 hover:text-ochre" data-testid="player-back">
            <ArrowLeft size={14} /> All players
          </Link>
          {isHof && player.rank ? (
            <p className="mt-8 font-display text-6xl font-bold leading-none text-ochre" data-testid="player-rank-badge">
              #{String(player.rank).padStart(2, "0")}
            </p>
          ) : null}
          <p className="overline mt-6 text-ochre">{POS_LABEL[player.position] || player.position}</p>
          <h1 className="display-1 mt-4" data-testid="player-name">{cleanName(player.name)}</h1>
          <div className="mt-8 grid max-w-lg grid-cols-2 gap-6 border-t border-white/20 pt-6 text-sm sm:grid-cols-4">
            <div><p className="overline !text-white/70">Club</p><p className="mt-1.5 text-white">{player.club || "—"}</p></div>
            <div><p className="overline !text-white/70">League</p><p className="mt-1.5 text-white">{player.league || "—"}</p></div>
            <div><p className="overline !text-white/70">Region</p><p className="mt-1.5 text-white">{player.region}</p></div>
            <div><p className="overline !text-white/70">{player.year ? "Signed" : "DOB"}</p><p className="mt-1.5 text-white">{player.year || player.dob || "—"}</p></div>
          </div>
          {player.value_usd ? (
            <div className="mt-6 max-w-lg border-l-2 border-ochre bg-[#0b0b0b] p-5" data-testid="player-peak-value">
              <p className="overline text-ochre">
                {player.value_estimated ? "Estimated peak value" : "Highest transfer fee"} · {player.value_as_at}
              </p>
              <p className="mono mt-2 text-lg font-bold text-white sm:text-xl" data-testid="player-peak-value-figures">
                {player.value_usd} <span className="text-zinc-500">/</span> {player.value_kes}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-zinc-400">{player.value_basis}</p>
            </div>
          ) : null}
          {socials.length ? (
            <div className="mt-8 flex flex-wrap gap-3" data-testid="player-socials">
              {socials.map(([key, Icon, label]) => (
                <a
                  key={key}
                  href={player[key]}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`player-social-${key}`}
                  className="border border-white/25 p-3 text-white/80 transition-colors hover:border-maasai hover:text-ochre"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          ) : null}

          <div className="mt-8 flex flex-wrap gap-3" data-testid="player-references">
            {player.transfermarkt_url ? (
              <a
                href={player.transfermarkt_url}
                target="_blank"
                rel="noreferrer"
                data-testid="player-transfermarkt"
                className="inline-flex items-center gap-2 border border-white/25 px-5 py-3 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-white/80 transition-colors hover:border-maasai hover:text-ochre"
              >
                Transfermarkt <ExternalLink size={13} />
              </a>
            ) : null}
            {player.wikipedia_url ? (
              <a
                href={player.wikipedia_url}
                target="_blank"
                rel="noreferrer"
                data-testid="player-wikipedia"
                className="inline-flex items-center gap-2 border border-white/25 px-5 py-3 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-white/80 transition-colors hover:border-maasai hover:text-ochre"
              >
                Wikipedia <ExternalLink size={13} />
              </a>
            ) : null}
            <a
                href={`${API}/player/${player.slug}.pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="player-pdf-link"
                className="inline-flex items-center gap-2 border border-white/25 px-5 py-3 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-white/80 transition-colors hover:border-maasai hover:text-ochre"
              >
                <Download size={14} /> Player PDF
              </a>
            <ShareCard
              title={`${cleanName(player.name)} — KickStartKenyaFootball`}
              text={
                player.rank
                  ? `${cleanName(player.name)} — #${player.rank} in the KickStartKenyaFootball Hall of Fame Top 20.`
                  : `${cleanName(player.name)} — Kenyan professional footballer, in the KickStartKenyaFootball archive.`
              }
              card={cardUrl("player", player.slug)}
              label="Share this player"
              testId="player-share"
            />
          </div>
        </div>
        <div className="relative flex items-center justify-center pb-10 lg:py-16">
          {player.portrait_url ? (
            <figure className="relative w-full max-w-[300px] lg:max-w-[420px]" data-testid="player-portrait">
              <img
                src={player.portrait_url}
                alt={cleanName(player.name)}
                className="w-full border border-white/15 object-cover grayscale-[0.15]"
                loading="lazy"
              />
              {player.portrait_credit ? (
                <figcaption className="mono mt-3 text-[0.55rem] uppercase tracking-[0.16em] text-white/45">
                  {player.portrait_credit}
                </figcaption>
              ) : null}
            </figure>
          ) : (
            <span
              aria-hidden="true"
              className="hidden select-none font-display text-[13rem] font-bold leading-none text-black/20 lg:block"
            >
              {initials(cleanName(player.name))}
            </span>
          )}
        </div>
      </section>

      <section className="border-t border-zinc-900 bg-black py-20 md:py-28">
        <div className="mx-auto grid max-w-[1500px] gap-16 px-5 md:px-10 lg:grid-cols-[1.2fr_1fr] [&>*]:min-w-0">
          <Reveal>
            <p className="overline">Profile</p>
            {player.bio_sections?.length ? (
              <div className="mt-6 space-y-7" data-testid="player-bio">
                {player.bio_sections.map((section) => (
                  <div key={section.heading} data-testid={`player-bio-section-${section.heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                    <h3 className="font-display text-base uppercase tracking-tight text-ochre md:text-lg">
                      {section.heading}
                    </h3>
                    <p className="mt-2 text-base leading-relaxed text-zinc-300 md:text-lg">{section.body}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-6 text-base leading-relaxed text-zinc-300 md:text-lg" data-testid="player-bio">{player.bio}</p>
            )}
            {player.cup_record ? (
              <div className="mt-8 border-l-2 border-maasai bg-[#0b0b0b] p-6" data-testid="player-cup-record">
                <p className="overline text-ochre">Cup and continental record</p>
                <p className="mt-3 text-sm leading-relaxed text-zinc-300 md:text-base">{player.cup_record}</p>
              </div>
            ) : null}
            {player.managers_note ? (              <div className="mt-6 border-l-2 border-zinc-700 bg-[#0b0b0b] p-6" data-testid="player-managers">
                <p className="overline text-ochre">Managers he played under</p>
                <p className="mt-3 text-sm leading-relaxed text-zinc-300 md:text-base">{player.managers_note}</p>
              </div>
            ) : null}
            <AfterFootball data={data.after_football} />
            {fact ? (
              <div className="mt-10">
                <DidYouKnow fact={fact} />
              </div>
            ) : null}
            {moreFacts.map((extra, i) => (
              <div className="mt-8" key={extra.headline}>
                <DidYouKnow fact={extra} testId={`did-you-know-more-${i}`} />
              </div>
            ))}
            {player.research_note || player.stats_source ? (
              <div className="mt-8 border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="player-research-note">
                <p className="overline text-ochre">Career record</p>
                {player.research_note ? (
                  <p className="mt-3 text-sm leading-relaxed text-zinc-300">{player.research_note}</p>
                ) : null}
                <p className="mt-4 break-words text-[0.66rem] uppercase tracking-[0.2em] text-zinc-400" data-testid="player-stats-source">
                  {player.stats_source || "Career record compiled from club, league and Transfermarkt records"}
                </p>
              </div>
            ) : null}

            {player.milestones?.length ? (
              <div className="mt-8 border border-maasai/50 bg-[#140406] p-6" data-testid="player-milestones">
                <p className="overline text-maasai">Firsts &amp; milestones</p>
                <ul className="mt-4 space-y-3">
                  {player.milestones.map((m, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-200">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 bg-maasai" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {player.competitions?.length ? (
              <div className="mt-8" data-testid="player-competitions">
                <p className="overline">Appearances by competition</p>
                <div className="mt-4 border border-zinc-800">
                  <div className="grid grid-cols-[1fr_58px_58px] gap-3 border-b border-zinc-800 bg-[#0b0b0b] px-4 py-2.5">
                    {["Competition", "Apps", "Goals"].map((h) => (
                      <span key={h} className="text-[0.55rem] font-bold uppercase tracking-[0.2em] text-zinc-400">
                        {h}
                      </span>
                    ))}
                  </div>
                  {player.competitions.map((c, i) => (
                    <div
                      key={i}
                      data-testid={`player-competition-${i}`}
                      className="grid grid-cols-[1fr_58px_58px] gap-3 border-b border-zinc-900 px-4 py-3 last:border-0"
                    >
                      <span className="text-sm leading-snug text-zinc-300">{c.competition}</span>
                      <span className="mono text-sm text-ochre">{c.apps}</span>
                      <span className="mono text-sm text-white">{c.goals}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[0.6rem] uppercase tracking-[0.2em] text-zinc-400">
                  Career approximations compiled from Transfermarkt and club records
                </p>
              </div>
            ) : null}

            <div className="mt-12 grid grid-cols-2 gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-3 lg:grid-cols-5">
              {stats.map(([label, value]) => (
                <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`player-stat-${label.toLowerCase()}`}>
                  <p className="font-display text-3xl font-bold text-white">{value || 0}</p>
                  <p className="mt-2 text-[0.65rem] uppercase tracking-[0.16em] text-zinc-500">{label}</p>
                </div>
              ))}
            </div>

            {player.career?.length ? (
              <div className="mt-14">
                <p className="overline">Career timeline</p>
                <ul className="mt-6 border-l border-zinc-800 pl-6" data-testid="player-timeline">
                  {player.career.map((c, i) => (
                    <li key={i} className="relative pb-8 last:pb-0">
                      <span className="absolute -left-[27px] top-1.5 h-2 w-2 rounded-full bg-maasai" />
                      <p className="font-display text-lg text-ochre">{c.year}</p>
                      <p className="body-copy mt-1">{c.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Reveal>

          <Reveal delay={0.12}>
            {isHof ? (
              <div className="border border-zinc-900 bg-ink-surface p-7">
                <p className="overline">AI score breakdown</p>
                <PlayerRadar scores={player.scores} />
                <div className="mt-6">
                  <ScoreTable scores={player.scores} total={player.total_score} />
                </div>
              </div>
            ) : (
              <div className="border border-zinc-900 bg-ink-surface p-7">
                <p className="overline">Listing</p>
                <h3 className="display-3 mt-4">
                  {player.list_type === "ones_to_watch" ? "Ones to Watch" : "Historic Top 20"}
                </h3>
                <p className="body-copy mt-4">
                  {player.list_type === "ones_to_watch"
                    ? "Flagged by our scouting network as a future senior international or European export."
                    : "Archived as part of Kenya's historic professional export record."}
                </p>
                <Link to="/hall-of-fame/leaderboard" className="btn-ghost mt-8">See the Hall of Fame Top 20</Link>
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {neighbours.previous || neighbours.next ? (
        <section className="border-t border-zinc-900 bg-ink-surface py-20 md:py-28" data-testid="player-neighbours">
          <div className="mx-auto max-w-[1500px] px-5 md:px-10">
            <p className="overline">{isHof ? "Either side of him in the ranking" : "Either side of him in the archive"}</p>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 [&>*]:min-w-0">
              {[["previous", "Ahead of him"], ["next", "Behind him"]].map(([key, label], i) =>
                neighbours[key] ? (
                  <div key={key} data-testid={`player-neighbour-${key}`}>
                    <p className="mono mb-3 text-[0.62rem] uppercase tracking-[0.16em] text-zinc-500">
                      {isHof
                        ? `${label} · Number ${neighbours[key].rank}`
                        : label === "Ahead of him"
                          ? "Previous in the archive"
                          : "Next in the archive"}
                    </p>
                    <PlayerCard player={neighbours[key]} index={i} />
                  </div>
                ) : null,
              )}
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
